import React from "react";
import { DateTime } from "luxon";
import { LineChart } from "@mui/x-charts/LineChart";
import Box from "@mui/material/Box";
import { BarChart } from "@mui/x-charts/BarChart";
import {
  useGameLevelResults,
  useGamePlayTestMetaData,
  useGamePlayTests,
  useMaxPlayers,
  useScoreParts,
  useScoreResults2,
  useScoreResults2WinnersOnly,
} from "@seabrookstudios/pitch2table";
import Typography from "@mui/material/Typography";
import { ScatterChart } from "@mui/x-charts";
import {
  PlayTestMediumLabels,
  formatPlace,
  PlayTestAudienceLabelShort,
  AudienceTypes,
  PlayTestMediumTypes,
  calculateMin,
  calculateMax,
  calculateMean,
  calculateStdDev,
  colorBlindFriendlyColors,
} from "@seabrookstudios/pitch2table-core";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AllInclusive from "@mui/icons-material/AllInclusive";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { ColorPatternDisplay, makePattern, makePatternId } from "./ColorPatternDisplay";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Chip from "@mui/material/Chip";

/**
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponent[]} scoreParts
 * @returns {React.ReactNode}
 */
const createSVGPatterns = (scoreParts) => {
  const patternDefs = scoreParts.map((m, index) => {
    const pattern = m.pattern || "none";
    const hasColour = m.colour && m.colour !== "#000000";
    const color = hasColour ? m.colour : colorBlindFriendlyColors[index % colorBlindFriendlyColors.length];
    return makePattern(pattern, index, color);
  });

  return (
    <svg style={{ position: "absolute", width: 0, height: 0 }}>
      <defs>{patternDefs}</defs>
    </svg>
  );
};

/**
 * @param {number} no
 */
const formatWinner = (no) => (no === 1 ? "Winner" : `Tied #${no}`);

// Guardrail against corrupted/overflow values from upstream payloads.
const MAX_REASONABLE_STAT_ABS = 1e12;

/**
 * @param {number | null | undefined} value
 */
const formatStatValue = (value) => {
  if (value === null || value === undefined) {
    return "N/A";
  }

  const absValue = Math.abs(value);
  if (absValue > 0 && absValue < 0.005) {
    return "~0";
  }

  // toFixed() returns scientific notation for very large numbers; keep output readable.
  if (absValue >= 1e21) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  return value.toFixed(2);
};

/**
 * @param {object} props
 * @param {Array} props.data - The chart data array
 * @param {number} props.seriesCount - Number of player positions
 * @param {boolean} props.winnersOnly - Whether showing winners only
 */
const StatisticsTable = ({ data, seriesCount, winnersOnly }) => {
  if (!data || data.length === 0) {
    return null;
  }

  // Extract data for each player position
  const positionData = [];
  for (let i = 1; i <= seriesCount; i++) {
    const values = data
      .map((item) => item[i])
      .map((val) => {
        if (val === null || val === undefined || val === "") {
          return NaN;
        }
        return typeof val === "number" ? val : Number(val);
      })
      .filter((val) => Number.isFinite(val) && Math.abs(val) <= MAX_REASONABLE_STAT_ABS);

    if (values.length > 0) {
      positionData.push({
        position: winnersOnly ? formatWinner(i) : formatPlace(i),
        min: calculateMin(values),
        mean: calculateMean(values),
        max: calculateMax(values),
        stdDev: calculateStdDev(values),
        count: values.length,
      });
    }
  }

  if (positionData.length === 0) {
    return null;
  }

  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        Player Statistics
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Position</TableCell>
              <TableCell align="right">Min</TableCell>
              <TableCell align="right">Mean</TableCell>
              <TableCell align="right">Max</TableCell>
              <TableCell align="right">Std Dev</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positionData.map((row) => (
              <TableRow key={row.position}>
                <TableCell component="th" scope="row">
                  {row.position}
                </TableCell>
                <TableCell align="right">{formatStatValue(row.min)}</TableCell>
                <TableCell align="right">{formatStatValue(row.mean)}</TableCell>
                <TableCell align="right">{formatStatValue(row.max)}</TableCell>
                <TableCell align="right">{formatStatValue(row.stdDev)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentId} props.metaId
 * @param {boolean} [props.winnersOnly]
 */
export const SingleTrackedDataChart = ({ gameId, metaId, winnersOnly = false }) => {
  const maxPlayers = useMaxPlayers(gameId, metaId);
  const allData = useScoreResults2(gameId, metaId);
  const winnersData = useScoreResults2WinnersOnly(gameId, metaId);
  const data = winnersOnly ? winnersData : allData;

  // When winnersOnly is true, we only want to show series for winners
  // We need to determine how many winners there are in the data
  const maxWinners =
    winnersOnly && winnersData.length > 0
      ? Math.max(
          ...winnersData.map(
            (item) =>
              Object.keys(item).filter(
                (key) => key !== "date" && key !== "no" && key !== "id" && item[key] !== null && item[key] !== undefined
              ).length
          )
        )
      : maxPlayers;

  const seriesCount = winnersOnly ? maxWinners : maxPlayers;

  return (
    <Box>
      <LineChart
        skipAnimation
        dataset={data}
        xAxis={[
          {
            dataKey: "no",
            valueFormatter: (v) => {
              if (!data[v]) {
                return "";
              }

              return DateTime.fromMillis(data[v].date).toFormat("D");
            },
          },
        ]}
        series={Array(seriesCount)
          .fill(0)
          .map((_, i) => i + 1)
          .map((no) => ({
            label: winnersOnly ? formatWinner(no) : formatPlace(no),
            dataKey: `${no}`,
            area: !winnersOnly,
            curve: "linear",
            showMark: false,
          }))}
        yAxis={[{ min: 0 }]}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "middle" },
          },
        }}
        height={500}
      />
      <Box p={1}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          {winnersOnly
            ? "Only play tests with winners that have a value for this data are included in the chart above."
            : "Only play tests that have a value for this data are included in the chart above."}
        </Typography>
      </Box>
      <StatisticsTable data={data} seriesCount={seriesCount} winnersOnly={winnersOnly} />
    </Box>
  );
};

export const CombinedScoreResults = SingleTrackedDataChart;

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentId} props.metaId
 */
export const CombinedScoreResultsWithToggle = ({ gameId, metaId }) => {
  const [winnersOnly, setWinnersOnly] = React.useState(false);

  const toggleWinnersOnly = () => {
    setWinnersOnly((x) => !x);
  };

  const ChartActions = () => {
    return (
      <Stack direction="row" pr={1}>
        <Tooltip title={"Toggle between showing all players or only winners"}>
          <IconButton onClick={toggleWinnersOnly}>{winnersOnly ? <AllInclusive /> : <EmojiEventsIcon />}</IconButton>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <ChartActions />
      </Box>
      <SingleTrackedDataChart gameId={gameId} metaId={metaId} winnersOnly={winnersOnly} />
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentId} props.metaId
 */
export const TrackGameLevelResult = ({ gameId, metaId }) => {
  const resultPerGame = useGameLevelResults(gameId, metaId);

  // Filter out non-numeric values to fix type error
  const filteredData = resultPerGame.filter((val) => typeof val === "number" && !isNaN(val)).map((val) => Number(val));

  return (
    <Box>
      <BarChart
        skipAnimation
        series={[{ data: filteredData }]}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "middle" },
          },
        }}
        yAxis={[{ min: 0, label: "Count" }]}
        height={500}
        bottomAxis={null}
      />
      <Box p={1}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          Only play tests that have a value are included in the chart above.
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {boolean} props.proportional
 * @param {boolean} props.winnersOnly
 */
export const ScoreCompositionChart = ({ gameId, proportional, winnersOnly }) => {
  const { data: metadata = [] } = useGamePlayTestMetaData(gameId);
  const scoreParts = metadata.filter((m) => m.type === "PART_OF_SCORE");

  // State to track which score components are toggled on/off
  const [toggledComponents, setToggledComponents] = React.useState(() => {
    // Initialize with archived items toggled off
    const initial = {};
    scoreParts.forEach((m) => {
      initial[m.id] = !m.archived;
    });
    return initial;
  });

  const byPosition = useScoreParts(
    gameId,
    scoreParts.map((m) => m.id),
    winnersOnly
  );

  // Handle toggle for a score component
  const handleToggleComponent = (componentId) => {
    setToggledComponents((prev) => ({
      ...prev,
      [componentId]: !prev[componentId],
    }));
  };

  return (
    <Box>
      {createSVGPatterns(scoreParts)}
      <BarChart
        skipAnimation
        dataset={byPosition}
        series={scoreParts.map((m, index) => {
          const pattern = m.pattern || "none";
          const hasColour = m.colour && m.colour !== "#000000";
          const color = hasColour ? m.colour : colorBlindFriendlyColors[index % colorBlindFriendlyColors.length];
          const isVisible = toggledComponents[m.id];

          return {
            dataKey: m.id,
            label: m.name,
            stack: "none",
            stackOffset: proportional ? "expand" : "none",
            scaleType: "band",
            color: isVisible ? `url(#${makePatternId(pattern, index, color)})` : "#cccccc",
            hidden: !isVisible,
          };
        })}
        yAxis={[{ min: 0, max: proportional ? 1 : undefined }]}
        slotProps={{
          legend: {
            hidden: true, // Hide the default legend
          },
        }}
        height={500}
      />

      {/* Custom legend below the chart */}
      <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={1}>
        <Typography variant="h6" gutterBottom>
          Score Components
        </Typography>
        <Box display="flex" flexDirection="column" gap={1}>
          {scoreParts.map((m) => {
            const isVisible = toggledComponents[m.id];
            return (
              <ListItem
                key={m.id}
                onClick={() => handleToggleComponent(m.id)}
                sx={{
                  opacity: isVisible ? 1 : 0.5,
                  filter: isVisible ? "none" : "grayscale(100%)",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemAvatar>
                  <ColorPatternDisplay
                    item={{
                      ...m,
                      pattern: isVisible ? m.pattern : "none",
                    }}
                    size={32}
                  />
                </ListItemAvatar>
                <ListItemText primary={m.name} />
                {m.archived && (
                  <ListItemSecondaryAction>
                    <Chip label="ARCHIVED" />
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            );
          })}
        </Box>
      </Box>

      <Box p={1}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          Only play tests that have at least one score part are included in the chart above. Click on legend items to
          toggle them on/off.
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const DurationOverTimeChart = ({ gameId }) => {
  const { data: playTests = [] } = useGamePlayTests(gameId);

  const playTestsWithDuration = playTests.filter((pt) => pt.duration && pt.ignore);

  const sortedPlayTests = playTestsWithDuration.sort((a, b) => a.date - b.date);

  const dataset = sortedPlayTests.map((pt, index) => ({
    no: index,
    date: pt.date,
    duration: pt.duration || 0,
    id: pt.id,
  }));

  return (
    <Box>
      <LineChart
        skipAnimation
        dataset={dataset}
        xAxis={[
          {
            dataKey: "date",
            valueFormatter: (v) => {
              // Handle both Date objects and timestamps
              const timestamp = v instanceof Date ? v.getTime() : v;
              return DateTime.fromMillis(timestamp).toFormat("D");
            },
            scaleType: "time",
          },
        ]}
        series={[
          {
            label: "Duration (minutes)",
            dataKey: "duration",
            curve: "linear",
            showMark: true,
            color: "#1976d2",
          },
        ]}
        yAxis={[
          {
            min: 0,
            label: "Duration (minutes)",
          },
        ]}
        slotProps={{
          legend: {
            direction: "horizontal",
            position: { vertical: "top", horizontal: "middle" },
          },
        }}
        height={500}
      />
      <Box p={1}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          Only play tests that have duration recorded are included in the chart above.
          {dataset.length > 0 && (
            <>
              {" "}
              Average duration: {(dataset.reduce((sum, pt) => sum + pt.duration, 0) / dataset.length).toFixed(1)}{" "}
              minutes.
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {boolean} props.groupByIntervals
 */
export const PlayerCountVsDurationChart = ({ gameId, groupByIntervals = false }) => {
  const { data: playTests = [] } = useGamePlayTests(gameId);

  // Filter play tests that have duration, players, and are not ignored
  const playTestsWithData = playTests.filter((pt) => pt.duration && pt.ignore && pt.outcomes.length > 0);

  // Helper function to round up to nearest 5-minute interval
  const roundUpToInterval = (minutes) => {
    return Math.ceil(minutes / 5) * 5;
  };

  // Create data for the scatter plot
  const data = playTestsWithData.map((pt) => ({
    x: pt.outcomes.length,
    y: groupByIntervals ? roundUpToInterval(pt.duration || 0) : pt.duration || 0,
    id: pt.id,
  }));

  // Calculate min and max player counts for axis range
  const minPlayers = data.length > 0 ? Math.min(...data.map((pt) => pt.x)) : 1;
  const maxPlayers = data.length > 0 ? Math.max(...data.map((pt) => pt.x)) : 1;

  if (data.length === 0) {
    return <Box p={1}>Insufficient data to display</Box>;
  }

  return (
    <Box>
      <ScatterChart
        height={500}
        xAxis={[
          {
            min: minPlayers,
            max: maxPlayers,
            tickNumber: maxPlayers - minPlayers + 1,
          },
        ]}
        yAxis={[
          {
            min: 0,
          },
        ]}
        series={[
          {
            label: "Play Tests",
            data: data,
            markerSize: 8,
          },
        ]}
      />
      <Box p={1}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          Each point represents a play test. Only play tests with both duration and player data are included.
          {groupByIntervals && " Durations are rounded up to 5-minute intervals."}
          {data.length > 0 && (
            <>
              {" "}
              Average duration per player:{" "}
              {(data.reduce((sum, pt) => sum + pt.y, 0) / data.reduce((sum, pt) => sum + pt.x, 0)).toFixed(
                1
              )} minutes.{" "}
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const AudienceDistributionChart = ({ gameId }) => {
  const { data: playTests = [] } = useGamePlayTests(gameId);

  // Filter play tests that are not ignored and have audience data
  const playTestsWithAudience = playTests.filter((pt) => pt.ignore && pt.audience);

  // Count by audience type
  const audienceCounts = {};
  playTestsWithAudience.forEach((pt) => {
    const audience = pt.audience;
    audienceCounts[audience] = (audienceCounts[audience] || 0) + 1;
  });

  // Convert to chart data format, including all audience types with zero counts
  const data = AudienceTypes.map((audienceType) => ({
    audience: PlayTestAudienceLabelShort[audienceType] || audienceType,
    count: audienceCounts[audienceType] || 0,
  }));

  return (
    <Box>
      <BarChart
        skipAnimation
        dataset={data}
        series={[
          {
            dataKey: "count",
            label: "Number of Play Tests",
            color: "#1976d2",
          },
        ]}
        xAxis={[
          {
            dataKey: "audience",
            scaleType: "band",
          },
        ]}
        yAxis={[{ min: 0, label: "Count" }]}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "middle" },
          },
        }}
        height={500}
      />
      <Box p={1}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          All audience types are shown in the chart above, including those with zero play tests.
          {data.length > 0 && <> Total play tests: {data.reduce((sum, item) => sum + item.count, 0)}.</>}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const CumulativePlayTestsChart = ({ gameId }) => {
  const { data: playTests = [] } = useGamePlayTests(gameId);

  // Filter play tests that are not ignored
  const validPlayTests = playTests.filter((pt) => pt.ignore);

  // Sort by date and create cumulative data
  const sortedPlayTests = validPlayTests.sort((a, b) => a.date - b.date);

  const cumulativeData = sortedPlayTests.map((pt, index) => ({
    date: pt.date,
    cumulative: index + 1,
    id: pt.id,
  }));

  return (
    <Box>
      <LineChart
        skipAnimation
        dataset={cumulativeData}
        xAxis={[
          {
            dataKey: "date",
            valueFormatter: (v) => {
              // Handle both Date objects and timestamps
              const timestamp = v instanceof Date ? v.getTime() : v;
              return DateTime.fromMillis(timestamp).toFormat("D");
            },
            scaleType: "time",
          },
        ]}
        series={[
          {
            label: "Cumulative Play Tests",
            dataKey: "cumulative",
            curve: "linear",
            showMark: true,
            color: "#1976d2",
          },
        ]}
        yAxis={[
          {
            min: 0,
            label: "Cumulative Count",
          },
        ]}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "middle" },
          },
        }}
        height={500}
      />
      <Box p={1}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          This chart shows the cumulative number of play tests over time.
          {cumulativeData.length > 0 && <> Total play tests: {cumulativeData[cumulativeData.length - 1].cumulative}.</>}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const MediumDistributionChart = ({ gameId }) => {
  const { data: playTests = [] } = useGamePlayTests(gameId);

  // Filter play tests that are not ignored and have medium data
  const playTestsWithMedium = playTests.filter((pt) => pt.ignore && pt.medium);

  // Count by medium type
  const mediumCounts = {};
  playTestsWithMedium.forEach((pt) => {
    const medium = pt.medium;
    mediumCounts[medium] = (mediumCounts[medium] || 0) + 1;
  });

  // Convert to chart data format, including all medium types with zero counts
  const data = PlayTestMediumTypes.map((mediumType) => ({
    medium: PlayTestMediumLabels[mediumType] || mediumType,
    count: mediumCounts[mediumType] || 0,
  }));

  return (
    <Box>
      <BarChart
        skipAnimation
        dataset={data}
        series={[
          {
            dataKey: "count",
            label: "Number of Play Tests",
            color: "#1976d2",
          },
        ]}
        xAxis={[
          {
            dataKey: "medium",
            scaleType: "band",
          },
        ]}
        yAxis={[{ min: 0, label: "Count" }]}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "middle" },
          },
        }}
        height={500}
      />
      <Box p={1}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          All medium types are shown in the chart above, including those with zero play tests.
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const PlayerCountDistributionChart = ({ gameId }) => {
  const { data: playTests = [] } = useGamePlayTests(gameId);

  // Filter play tests that are not ignored and have player data
  const playTestsWithPlayers = playTests.filter((pt) => pt.ignore && pt.outcomes.length > 0);

  // Count by player count
  const playerCounts = {};
  playTestsWithPlayers.forEach((pt) => {
    const playerCount = pt.outcomes.length;
    playerCounts[playerCount] = (playerCounts[playerCount] || 0) + 1;
  });

  // Convert to chart data format and sort by player count
  const data = Object.entries(playerCounts)
    .map(([playerCount, count]) => ({
      playerCount: parseInt(playerCount),
      count,
    }))
    .sort((a, b) => a.playerCount - b.playerCount);

  return (
    <Box>
      <BarChart
        skipAnimation
        dataset={data}
        series={[
          {
            dataKey: "count",
            label: "Number of Play Tests",
            color: "#1976d2",
          },
        ]}
        xAxis={[
          {
            dataKey: "playerCount",
            scaleType: "band",
            label: "Number of Players",
          },
        ]}
        yAxis={[{ min: 0, label: "Count" }]}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "middle" },
          },
        }}
        height={500}
      />
      <Box p={1}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          Only play tests with recorded player data are included in the chart above.
          {data.length > 0 && <> Total play tests: {data.reduce((sum, item) => sum + item.count, 0)}.</>}
        </Typography>
      </Box>
    </Box>
  );
};
